"use client";

import { Button, buttonVariants } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Copy, LoaderCircle } from "lucide-react";
import { useState } from "react";
import { client } from "@/lib/hono";
import toast from "@/lib/toast";
import { GetPresentationResponseType } from "../../../hooks/useGetPresentation";
import { useStorage } from "@/features/storage/useStorage";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";

interface DuplicatePresentationButtonProps {
    presentation: GetPresentationResponseType["data"];
}

export default function DuplicatePresentationButton({
    presentation,
}: DuplicatePresentationButtonProps) {
    const setOpen = useStorage((state) => state.setPresentationSheetOpen);

    const [isPending, setIsPending] = useState(false);

    const queryClient = useQueryClient();

    const closeModal = () => {
        setOpen(false);
        setIsPending(false);
    };

    const submitHandler = async (e: any) => {
        e.preventDefault();
        setIsPending(true);

        const newName = e.target.name.value as string;

        const res = await client.api.storage.presentation.duplicate.$post({
            json: {
                newName,
                projectIdToCopy: presentation.id,
            },
        });

        if (!res.ok) {
            const error = await res.text();
            console.log(error);
            toast.error(t("toast.error.duplicate-presentation"), {
                keyName: "toast.error.duplicate-presentation",
            });
        } else {
            const data = await res.json();
            console.log(data);

            queryClient.invalidateQueries({ queryKey: ["get-storage-files"] });

            toast.success(t("toast.success.duplicate-presentation"), {
                keyName: "toast.success.duplicate-presentation",
            });
            closeModal();
        }
    };

    return (
        <Dialog>
            <DialogTrigger>
                <div
                    className={cn(
                        buttonVariants({
                            variant: "secondary",
                            size: "sm",
                        })
                    )}
                >
                    <Copy className="size-3.5 me-2" />
                    {t("presentation-sheet.header.duplicate-btn", undefined, undefined)}
                </div>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[380px]">
                <DialogHeader>
                    <DialogTitle>
                        {t("presentation.action-modals.duplicate.title", undefined, undefined)}
                    </DialogTitle>
                    <DialogDescription className="">
                        {t("presentation.action-modals.duplicate.description", undefined, undefined)}
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submitHandler}>
                    <div className="w-full pb-6">
                        <Label className="flex w-full justify-start mb-2">
                            {t("presentation.action-modals.duplicate.name-input.label", undefined, undefined)}
                        </Label>
                        <Input
                            id="name"
                            defaultValue={
                                presentation?.name
                                    ? t(
                                          "presentation.action-modals.duplicate.default-name",
                                          undefined,
                                          { name: presentation.name }
                                      )
                                    : ""
                            }
                            required
                        />
                    </div>
                    <DialogFooter>
                        <Button type="submit" disabled={isPending}>
                            {isPending ? (
                                <div className="flex w-full justify-center items-center">
                                    <LoaderCircle className="size-4 text-white animate-spin" />
                                </div>
                            ) : (
                                t("presentation.action-modals.duplicate.submit-btn", undefined, undefined)
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
